import { Avatar } from '@assembly-js/design-system';
import { MessageResponse } from 'stream-chat';
import { makeStyles } from 'tss-react/mui';

import { LoadingWrapper } from 'src/legacy/components/Loading';
import StandardPage from 'src/legacy/components/UI/StandardPage';
import { GraySmall, highlight, NonHoverBorder, white } from 'src/theme/colors';
import { DateUtils } from 'src/utils';
import { getInitials } from 'src/utils/UserUtils';

import MessageTextRenderer from './MessageTextRenderer';

type Props = {
  query: string;
  queriedMessages: MessageResponse[];
  isLoading: boolean;
  onMessageClick: (messageId: string) => void;
};

const useStyles = makeStyles()((theme) => ({
  searchResultContainer: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(0, 2.5, 3),
    overflowY: 'auto',
    height: 'calc(100% - 100px)',
  },
  searchResultCard: {
    width: '100%',
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2, 2, 2.5, 2),
    borderRadius: 4,
    border: `1px solid ${NonHoverBorder}`,
    '&:hover': {
      cursor: 'pointer',
      boxShadow: '0px 6px 20px 0px rgba(0, 0, 0, 0.07)',
      backgroundColor: white,
      transition: '.2s ease',
    },
  },
  userMessageDetails: {
    width: '100%',
    marginTop: theme.spacing(1),
    display: 'flex',
    flexDirection: 'row',
  },
  resultLength: {
    color: GraySmall,
    fontSize: 12,
    fontWeight: 500,
    padding: theme.spacing(3, 2.5, 2.5),
  },
  searchResultTime: {
    color: GraySmall,
    fontSize: 12,
    fontWeight: 500,
  },
  senderDetails: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  senderName: {
    color: '#0E0E10',
    fontSize: 14,
    fontWeight: 500,
  },
  userMessage: {
    color: '#313137',
    fontSize: 13,
    fontWeight: 400,
    margin: 0,
    width: '100%',
    '& p': {
      margin: 0,
    },
    '& p > span': {
      backgroundColor: highlight,
      borderRadius: 2,
      padding: theme.spacing(0, 0.2),
    },
  },
}));

const standardPageParams = {
  titleForClient: 'No matches found',
  descForClient: 'Check for typos or try a different search query.',
  title: 'No matches found',
  desc: 'Check for typos or try a different search query.',
  showTopIcon: false,
};

export const QueriedMessagesList = ({
  query,
  queriedMessages,
  isLoading,
  onMessageClick,
}: Props) => {
  const { classes } = useStyles();
  if (!query) return null;

  return (
    <>
      <LoadingWrapper fullHeight isLoading={isLoading} hideContentWhileLoading>
        <>
          {queriedMessages.length === 0 && !isLoading ? (
            <StandardPage {...standardPageParams} />
          ) : (
            <>
              <div className={classes.resultLength}>
                {`Showing ${queriedMessages?.length} results for "${query}"`}
              </div>
              <div className={classes.searchResultContainer}>
                {queriedMessages?.map((result) => (
                  <div
                    key={result?.id}
                    className={classes.searchResultCard}
                    onClick={() => onMessageClick(result.id)}
                  >
                    <span className={classes.searchResultTime}>
                      {DateUtils.formatDate(
                        result.created_at as string,
                        `MM/DD/YYYY [at] hh:mm`,
                      )}
                    </span>
                    <div className={classes.userMessageDetails}>
                      <Avatar
                        src={result?.user?.image ?? ''}
                        fallbackColor={result?.user?.fallbackColor ?? ''}
                        variant="circle"
                        text={getInitials(result?.user?.name ?? '')}
                        size="lg"
                        alt={result?.user?.name ?? ''}
                        className="mr-4"
                      />
                      <div className={classes.senderDetails}>
                        <span className={classes.senderName}>
                          {result?.user?.name}
                        </span>
                        <div className={classes.userMessage}>
                          <MessageTextRenderer result={result} query={query} />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      </LoadingWrapper>
    </>
  );
};
